import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

export type CaseResolveAction = 'attend' | 'reject';

interface CaseResolveDialogProps {
  action: CaseResolveAction;
  open: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (resolutionNote: string) => void;
}

const ACTION_COPY: Record<CaseResolveAction, { title: string; description: string; confirmLabel: string }> = {
  attend: {
    title: 'Marcar evaluación como atendida',
    description: 'Deja opcionalmente una nota de qué se hizo para resolverlo.',
    confirmLabel: 'Marcar atendido',
  },
  reject: {
    title: 'Rechazar evaluación',
    description: 'Deja opcionalmente una nota de por qué se rechaza.',
    confirmLabel: 'Rechazar',
  },
};

const CaseResolveDialog = ({ action, open, isLoading, onCancel, onConfirm }: CaseResolveDialogProps): JSX.Element => {
  const [note, setNote] = useState('');
  const copy = ACTION_COPY[action];

  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => (!isOpen ? onCancel() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota de resolución (opcional)"
          rows={4}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant={action === 'reject' ? 'destructive' : 'default'}
            onClick={() => onConfirm(note.trim())}
            disabled={isLoading}
          >
            {isLoading ? 'Procesando…' : copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CaseResolveDialog;
